import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Stack,
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { Can } from 'core/components/permissions/Can';
import {
  currencyFormatter,
  dateFormatter,
  dateTimeFormatter,
  getFullName,
  isNumeric,
} from 'core/utils/commonUtils';
import { currencyCol } from 'core/utils/muiGridUtils';
import { getOrderPaymentTypeName } from 'core/utils/orderUtils';
import useOrderPayments, {
  SWR_ORDER_PAYMENTS_ENDPOINT,
} from 'hooks/swr/useOrderPayments';
import useResponsive from 'hooks/useResponsive';
import { isArray, isNil } from 'lodash';
import { observer } from 'mobx-react-lite';
import { OrderPaymentTypeEnum } from 'models/enums/orderPaymentTypeEnum';
import { useContext, useState } from 'react';
import * as orderPaymentService from 'services/orderPaymentService';
import StoreContext from 'stores/context/StoreContext';
import { mutate } from 'swr';
import PaymentEditDialog from './PaymentEditDialog';
import type { GridColDef } from '@mui/x-data-grid';
import type { OrderPaymentInfo } from 'models/order/orderPaymentInfo';
import type { FC } from 'react';
import type { CreateOrUpdateOrderPaymentRequest } from 'services/dtoModels/order/createOrUpdateOrderPaymentRequest';

const OrderPaymentsEdit: FC<OrderPaymentsEditProps> = observer(({ orderId }) => {
  // const orderPageStore = useOrderPageStore();
  const isSmScreen = useResponsive('down', 'sm');
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [editedPayment, setEditedPayment] = useState<OrderPaymentInfo>();
  const { data: payments, isLoading } = useOrderPayments(
    orderId,
    isNumeric(orderId ?? '')
  );

  const { userStore } = useContext(StoreContext);

  const handlePaymentDialogCancel: VoidFunction = () => {
    setPaymentDialogOpen(false);
  };

  const handlePaymentDialogSave = async (payment: OrderPaymentInfo): Promise<boolean> => {
    const isNew = !isNil(editedPayment) && editedPayment.id <= 0;

    const request: CreateOrUpdateOrderPaymentRequest = {
      datePayment: payment.datePayment,
      orderId,
      remark: payment.remark,
      sum: payment.sum,
      type: payment.type,
    };

    try {
      const query = isNew
        ? orderPaymentService.createOrderPaymentAsync(+(orderId ?? 0), request)
        : orderPaymentService.updateOrderPaymentAsync(
            +(orderId ?? 0),
            payment.id,
            request
          );

      await query;

      await mutate(
        (key) => isArray(key) && key[0] === SWR_ORDER_PAYMENTS_ENDPOINT,
        undefined
      );

      setPaymentDialogOpen(false);
      setEditedPayment(undefined);

      return true;
    } catch {
      return false;
    }
  };

  const handleAddPayment = (): void => {
    setEditedPayment({
      id: -Date.now(),
      dateCreate: new Date(),
      datePayment: new Date(),
      orderId: 0,
      remark: '',
      sum: 0,
      type: OrderPaymentTypeEnum.Cash,
      userId: userStore.userData?.id ?? 0,
      user: {
        id: userStore.userData?.id ?? 0,
        fullName: !isNil(userStore.userData) ? getFullName(userStore.userData) : '',
      },
    });
    setPaymentDialogOpen(true);
  };

  const handleEdit = (payment: OrderPaymentInfo): void => {
    setEditedPayment(payment);
    setPaymentDialogOpen(true);
  };

  const columns: Array<GridColDef<OrderPaymentInfo>> = [
    {
      field: 'dateCreate',
      headerName: 'Дата',
      valueFormatter: (value) => dateTimeFormatter.format(value),
      minWidth: 150,
    },
    {
      field: 'sum',
      headerName: 'Сумма',
      ...currencyCol,
    },
    {
      field: 'type',
      headerName: 'Тип',
      valueGetter: (value) => getOrderPaymentTypeName(value),
      flex: 0.3,
    },
    {
      field: 'owner',
      headerName: 'Создал',
      valueGetter: (_, row) => row.user?.fullName ?? '',
      flex: 0.6,
    },
    { field: 'remark', headerName: 'Примечание', flex: 1 },
    ...(userStore.ability?.can('update', 'Payments') ?? false
      ? [
          {
            field: 'actions',
            maxWidth: 40,
            type: 'actions',
            getActions: (params) => [
              <GridActionsCellItem
                icon={<EditIcon />}
                key={params.row.id}
                onClick={() => {
                  handleEdit(params.row);
                }}
                label='Edit'
              />,
            ],
          } satisfies GridColDef<OrderPaymentInfo>,
        ]
      : []),
  ];

  if (isLoading) {
    return <Skeleton variant='rounded' height={200} />;
  }

  return (
    <Stack spacing={2}>
      <Box>
        <Button variant='outlined' onClick={handleAddPayment}>
          Добавить
        </Button>
      </Box>

      {isSmScreen ? (
        <List disablePadding>
          {payments?.map((payment) => (
            <ListItem
              key={payment.id}
              disablePadding
              secondaryAction={
                <IconButton
                  edge='end'
                  aria-label='edit'
                  onClick={() => {
                    handleEdit(payment);
                  }}>
                  <Can I='update' a='Payments'>
                    <EditIcon />
                  </Can>
                </IconButton>
              }>
              <ListItemText
                primary={`${currencyFormatter.format(
                  Number(payment.sum)
                )} от ${dateFormatter.format(payment.dateCreate)}`}
                secondary={`${getOrderPaymentTypeName(payment.type)}. ${payment.remark}`}
              />
            </ListItem>
          ))}
        </List>
      ) : (
        <DataGrid
          rows={payments ?? []}
          columns={columns}
          hideFooter
          disableRowSelectionOnClick
          autoHeight
        />
      )}

      {editedPayment && (
        <PaymentEditDialog
          open={paymentDialogOpen}
          payment={editedPayment}
          onCancel={handlePaymentDialogCancel}
          onSave={handlePaymentDialogSave}
        />
      )}
    </Stack>
  );
});

export interface OrderPaymentsEditProps {
  orderId: number;
}

export default OrderPaymentsEdit;
